import { useCallback } from 'react';
import { updateGuideFields } from '@/data/guidesRepo';
import type { HistoryInfoType } from '@/types/types_history';
import { useCurrentUserSimple } from './useCurrentUserSimple';
import { applyHistoryToUpdate, type HistorizedSaveType } from './util_history';
import type { SaveStack } from './util_saveStack';

/**
 * Returns an `autosave(userAction, fields, currentHistory)` function that writes
 * changed fields of one guide to Firestore, registering the operation on the
 * save stack. Mirrors useAutosavePartner.
 */
export function useAutosaveGuide(guideId: string | undefined, saveStack: SaveStack) {
  const userSimple = useCurrentUserSimple();

  return useCallback(
    (
      userAction: string,
      fields: Record<string, unknown>,
      currentHistory: HistoryInfoType | undefined,
      isUndoStep = false,
    ): HistorizedSaveType => {
      if (!guideId) {
        throw new Error(`Cannot autosave: no guide id. action=[${userAction}]`);
      }
      const { updateObj, newHistory } = applyHistoryToUpdate(
        fields,
        userAction,
        currentHistory,
        userSimple,
        isUndoStep,
      );

      const opId = crypto.randomUUID();
      saveStack.pushToSaveStack({ id: opId, userAction });
      const saved = updateGuideFields(guideId, updateObj).then(() => {
        saveStack.removeFromSaveStack(opId);
      });

      return { newHistory, saved };
    },
    [guideId, saveStack, userSimple],
  );
}
